// SearchResults.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast as message } from 'sonner';
import './SearchResults.css';
import { executeTableSearch } from '../utils/search';
import { getSearchableTableNames } from '../utils/searchableTables';
import { useGlobalMetadata } from '../utils/providers';
import TableGrid from './TableGrid/index';
import type { BaseRecord } from '../types/api';
import type { GridHeightLayout } from '../types/grid';
import { useOpenSmartHit, useSmartSearch } from './SmartSearch/useSmartSearch';
import { SmartHitList } from './SmartSearch/SmartHitList';
import { ALL_SMART_ENTITIES, orderRows } from '../utils/smartSearch';
import './SmartSearch/SmartSearch.css';

// Smart search (REG-742) covers these; the per-table exact search below
// covers every other searchable table.
const SMART_TABLES = new Set(['merchant', 'submission', 'rep', 'funder', 'lead_source']);

/** The same engine as the header search, with more rows per type. */
function SmartResults({ query }: { query: string }) {
  const { hits, isFetching } = useSmartSearch(query, ALL_SMART_ENTITIES, 25);
  const rows = orderRows(hits);
  const openHit = useOpenSmartHit();
  const [highlighted, setHighlighted] = useState(-1);
  return (
    <div className="sky-result-card smart-results-card" data-testid="smart-results">
      <h3 className="sky-result-card-title">Best matches</h3>
      <SmartHitList
        rows={rows}
        highlighted={highlighted}
        onHighlight={setHighlighted}
        onSelect={(row) => { if (row.kind === 'hit') openHit(row.hit, query); }}
        isFetching={isFetching}
        query={query}
      />
    </div>
  );
}

interface SearchResultsState {
  [tableName: string]: BaseRecord[];
}

interface GridConfigResult {
  detailRouteName?: string;
  listConfig: {
    ui_data: {
      heightLayout: GridHeightLayout;
      showPagination: boolean;
      isEditable: boolean;
      showMenu: boolean;
      showFilters: boolean;
      showAdd: boolean;
      showDelete: boolean;
      showViewRecord: boolean;
      fields: any[];
    };
    table_name: string;
    name: string;
  };
}

const SearchResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: metadata } = useGlobalMetadata();
  const [results, setResults] = useState<SearchResultsState>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);

  const query = new URLSearchParams(location.search).get('query');
  const [localQuery, setLocalQuery] = useState(query || '');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Sync local query when URL changes externally
  useEffect(() => {
    setLocalQuery(query || '');
  }, [query]);

  const handleQueryChange = (value: string) => {
    setLocalQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Clear immediately when input is empty
    if (!value.trim()) {
      setResults({});
      navigate('/search', { replace: true });
      return;
    }

    debounceRef.current = setTimeout(() => {
      navigate(`/search?query=${encodeURIComponent(value.trim())}`, { replace: true });
    }, 400);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (localQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(localQuery.trim())}`, { replace: true });
    } else {
      navigate('/search', { replace: true });
    }
  };

  const searchableTables = useMemo(() => {
    if (!metadata?.fieldData?.flattened) return [];
    return getSearchableTableNames(
      Object.values(metadata.fieldData.flattened),
      metadata.tableData?.byId ?? {},
      metadata.routeData?.primaryUiComponentByTableName ?? {},
    ).filter((tableName) => !SMART_TABLES.has(tableName));
  }, [metadata?.fieldData?.flattened, metadata?.tableData?.byId, metadata?.routeData?.primaryUiComponentByTableName]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setResults({});
    setIsLoading(Boolean(query));

    const fetchResults = async (): Promise<void> => {
      if (query) {
        const promises = searchableTables
          .filter(tableName => {
            const tableInfo = metadata?.tableData?.byName?.[tableName];
            return tableInfo && tableInfo.table_name;
          })
          .map(async (tableName: string) => {
            try {
              const tableInfo = metadata?.tableData?.byName?.[tableName];
              const schema = tableInfo?.schema_name || 'public';
              const data = await executeTableSearch({
                searchTerm: query,
                tableName,
                fields: metadata?.fieldData?.[tableName]?.asList,
                schema
              });
              return { [tableName]: data };
            } catch (error: any) {
              console.error(`Error searching table ${tableName}:`, error);
              message.error(`Error searching table ${tableName}: ${error.message}`);
              return { [tableName]: [] };
            }
          });

        const results = await Promise.all(promises);
        const resultsObject = results.reduce((acc, result) => ({ ...acc, ...result }), {});
        if (requestId === requestIdRef.current) setResults(resultsObject);
      }
      if (requestId === requestIdRef.current) setIsLoading(false);
    };

    void fetchResults();
    return () => { requestIdRef.current += 1; };
  }, [query, searchableTables, metadata?.fieldData, metadata?.tableData]);

  const searchHeader = (
    <form className="sky-global-search-form" onSubmit={handleSearchSubmit}>
      <svg className="sky-global-search-icon" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
      </svg>
      <input
        ref={inputRef}
        className="sky-global-search-input"
        type="text"
        placeholder="Search name, phone, email, #deal, or any record..."
        value={localQuery}
        onChange={e => handleQueryChange(e.target.value)}
      />
    </form>
  );

  if (isLoading && query) {
    return (
      <div data-page-loading="true">
        {searchHeader}
        <SmartResults query={query} />
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><span className="sky-spinner" /></div>
      </div>
    );
  }

  if (!query) {
    return (
      <div>
        {searchHeader}
        <div className="sky-empty-state">
          <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3 }}>
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <div className="sky-empty-title">Global Search</div>
          <div className="sky-empty-desc">Type a query above and press Enter to search across all tables</div>
        </div>
      </div>
    );
  }

  if (!results || Object.keys(results).length === 0) {
    return (
      <div>
        {searchHeader}
        <SmartResults query={query} />
        <div className="sky-empty-state">
          <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.3 }}>
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
          <div className="sky-empty-title">No other records</div>
          <div className="sky-empty-desc">No other records match &quot;{query}&quot;</div>
        </div>
      </div>
    );
  }

  const getGridConfig = (tableName: string): GridConfigResult | null => {
    const hasDetailPage = metadata?.routeData?.primaryUiComponentByTableName[tableName]?.detail ? true : false;
    const hasSearchConfig = metadata?.routeData?.primaryUiComponentByTableName[tableName]?.search ? true : false;

    if (!hasSearchConfig) return null;

    return {
      detailRouteName: metadata?.routeData?.tableToRouteName[tableName],
      listConfig: {
        ui_data: {
          heightLayout: 'autoHeight' as GridHeightLayout,
          showPagination: false,
          isEditable: false,
          showMenu: false,
          showFilters: false,
          showAdd: false,
          showDelete: false,
          showViewRecord: hasDetailPage,
          fields: metadata?.routeData?.primaryUiComponentByTableName[tableName]?.search?.ui_data?.fields || [],
        },
        table_name: tableName,
        name: metadata?.routeData?.primaryUiComponentByTableName[tableName]?.search?.name || tableName
      }
    };
  };

  const resultComponents = Object.entries(results).map(([tableName, tableData]) => {
    let resultComponent: React.ReactNode = null;
    const gridConfig = getGridConfig(tableName);

    if (!gridConfig) return null;
    if (tableData.length === 0) {
      resultComponent = <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '8px 0' }}>No results</div>;
    } else {
      resultComponent = (
        <TableGrid
          key={tableName}
          gridConfig={gridConfig.listConfig}
          controlledData={tableData}
          routeName={gridConfig.detailRouteName || ''}
        />
      );
    }

    return (
      <div key={tableName} className="sky-result-card">
        <h3 className="sky-result-card-title">{metadata?.tableData?.byName?.[tableName]?.ui_label || tableName}</h3>
        {resultComponent}
      </div>
    );
  });

  return (
    <div className="search-results-container">
      {searchHeader}
      <SmartResults query={query} />
      {resultComponents}
    </div>
  );
};

export default SearchResults;
